// /Users/pramod/Desktop/school-erp/frontend/src/Head-Of-Institution/Layout.js

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { LogOut, School, Menu, X, Shield, Award, Crown } from 'lucide-react';
import logoutManager from '../logout';
import api from '../api';

const Layout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [, setIsProfileDropdownOpen] = useState(false);
  
  // ============ DIRECT STATE FOR TENANT DATA ============
  const [tenantData, setTenantData] = useState({
    institutionId: null,
    institutionName: 'Truyon',
    logoUrl: null,
    profileName: 'Admin'
  });
  const [loading, setLoading] = useState(true);

  // Get user role from localStorage
  const getUserRole = () => {
    const user = localStorage.getItem('user');
    if (user) {
      try {
        const parsed = JSON.parse(user);
        return parsed.role || parsed.userRole || 'head_of_institution';
      } catch (e) {
        return 'head_of_institution';
      }
    }
    return 'head_of_institution';
  };

  const userRole = getUserRole();

  // Role-specific configurations
  const roleConfig = {
    head_of_institution: {
      title: 'Head of Institution',
      icon: <Crown size={14} className="text-amber-400" />,
      badgeColor: 'bg-amber-100 text-amber-700 border-amber-200',
      welcomeMessage: 'Welcome back, Head of Institution!',
      description: 'You have full access to institutional management and oversight.',
      roleLabel: 'Head of Institution'
    },
    hod: {
      title: 'Head of Department',
      icon: <Shield size={14} className="text-blue-400" />,
      badgeColor: 'bg-blue-100 text-blue-700 border-blue-200',
      welcomeMessage: 'Welcome back, Head of Department!',
      description: 'You have access to department management and faculty oversight.',
      roleLabel: 'Head of Department'
    },
    principal: {
      title: 'Principal',
      icon: <Award size={14} className="text-purple-400" />,
      badgeColor: 'bg-purple-100 text-purple-700 border-purple-200',
      welcomeMessage: 'Welcome back, Principal!',
      description: 'You have full access to institutional management and academic oversight.',
      roleLabel: 'Principal'
    }
  };

  const currentRoleConfig = roleConfig[userRole] || roleConfig.head_of_institution;

  // Navigation items
  const navItems = [
    { path: '/head-of-institution/dashboard', label: 'Dashboard', icon: '📊' },
    { path: '/head-of-institution/students', label: 'Students', icon: '👥' },
    { path: '/head-of-institution/faculty', label: 'Faculty', icon: '👨‍🏫' },
    { path: '/head-of-institution/classes', label: 'Classes', icon: '📚' },
    { path: '/head-of-institution/attendance', label: 'Attendance', icon: '📋' },
    { path: '/head-of-institution/performance', label: 'Performance', icon: '📈' },
    { path: '/head-of-institution/departments', label: 'Departments', icon: '🏛️' },
    { path: '/head-of-institution/calendar', label: 'Calendar', icon: '📅' },
    { path: '/head-of-institution/reports', label: 'Reports', icon: '📄' },
    { path: '/head-of-institution/settings', label: 'Settings', icon: '⚙️' },
  ];

  // ============ GET BASE URL FROM API ============
  const getBaseUrl = () => {
    // Get base URL from api.defaults.baseURL
    if (api.defaults && api.defaults.baseURL) {
      return api.defaults.baseURL;
    }
    // Fallback: use window.location origin
    return window.location.origin;
  };

  // ============ HELPER: Get full image URL ============
  const getFullImageUrl = useCallback((url) => {
    if (!url) return null;
    
    // If already a full URL, return as is
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    
    // If it's a relative path starting with /api
    if (url.startsWith('/api')) {
      const baseUrl = getBaseUrl();
      return `${baseUrl}${url}`;
    }
    
    // If it's a relative path without /api
    if (url.startsWith('/')) {
      const baseUrl = getBaseUrl();
      return `${baseUrl}${url}`;
    }
    
    // Otherwise, treat as is
    return url;
  }, []);

  // ============ DIRECT API FETCH FUNCTION ============
  const fetchTenantInfo = useCallback(async () => {
    try {
      setLoading(true);
      const userId = localStorage.getItem('userId');
      const tenantId = localStorage.getItem('tenantId');
      const token = localStorage.getItem('token');

      console.log('🔍 Directly fetching tenant info...', { userId, tenantId });

      if (!userId || !tenantId || !token) {
        console.warn('⚠️ Missing required auth data');
        setLoading(false);
        return;
      }

      // Fetch from API directly
      console.log('📡 Fetching fresh tenant data from API...');
      
      // Try multiple endpoints
      let institutionName = null;
      let institutionLogo = null;
      let profileName = 'Admin';
      let institutionIdResult = tenantId;

      // Try to get basic info
      try {
        const basicInfoResponse = await api.get(`/api/organization/basic-info/${tenantId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'x-tenant-id': tenantId
          }
        });
        
        if (basicInfoResponse.data) {
          const data = basicInfoResponse.data;
          institutionName = data.name || data.organizationName || data.orgName || institutionName;
          institutionLogo = data.logoUrl || data.logo || data.logo_url || institutionLogo;
        }
      } catch (error) {
        console.warn('Basic info endpoint failed:', error.message);
      }

      // Try organization endpoint
      if (!institutionName) {
        try {
          const orgResponse = await api.get(`/api/organization/${tenantId}`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'x-tenant-id': tenantId
            }
          });
          
          if (orgResponse.data) {
            const data = orgResponse.data;
            institutionName = data.name || data.organizationName || data.orgName || institutionName;
            institutionLogo = data.logoUrl || data.logo || data.logo_url || institutionLogo;
            institutionIdResult = data.tenantId || data._id || tenantId;
          }
        } catch (error) {
          console.warn('Organization endpoint failed:', error.message);
        }
      }

      // Try user endpoint for profile name
      try {
        const userResponse = await api.get(`/api/users/${userId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'x-tenant-id': tenantId
          }
        });
        
        if (userResponse.data) {
          profileName = userResponse.data.name || userResponse.data.firstName || profileName;
          if (!institutionName) {
            institutionName = userResponse.data.institutionName || userResponse.data.institution?.name || institutionName;
          }
          if (!institutionLogo) {
            institutionLogo = userResponse.data.institutionLogo || userResponse.data.institution?.logoUrl || institutionLogo;
          }
        }
      } catch (error) {
        console.warn('User endpoint failed:', error.message);
      }

      // Try tenant info endpoint
      if (!institutionName) {
        try {
          const tenantResponse = await api.get(`/api/head-of-institution/tenant/${userId}`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'x-tenant-id': tenantId
            }
          });
          
          if (tenantResponse.data && tenantResponse.data.success) {
            const data = tenantResponse.data.data;
            institutionName = data.institutionName || data.tenantName || data.name || institutionName;
            institutionLogo = data.logoUrl || data.logo || data.branding?.logo || institutionLogo;
            profileName = data.profileName || data.adminName || data.name || profileName;
            institutionIdResult = data.institutionId || data.tenantId || tenantId;
          }
        } catch (error) {
          console.warn('Tenant info endpoint failed:', error.message);
        }
      }

      // If still no name, use tenantId as fallback
      if (!institutionName || institutionName === 'Truyon') {
        institutionName = tenantId || 'Truyon';
      }

      // FIXED: Use getFullImageUrl instead of hardcoding localhost
      const fullLogoUrl = getFullImageUrl(institutionLogo);

      const newTenantData = {
        institutionId: institutionIdResult,
        institutionName: institutionName || 'Truyon',
        logoUrl: fullLogoUrl,
        profileName: profileName || 'Admin'
      };

      console.log('✅ Tenant data fetched:', newTenantData);
      setTenantData(newTenantData);

    } catch (error) {
      console.error('❌ Error fetching tenant info:', error);
    } finally {
      setLoading(false);
    }
  }, [getFullImageUrl]);

  // ============ FETCH ON MOUNT ============
  useEffect(() => {
    fetchTenantInfo();
  }, [fetchTenantInfo]);

  // ============ CLEAR TENANT DATA ON LOGOUT ============
  const clearTenantData = () => {
    setTenantData({
      institutionId: null,
      institutionName: 'Truyon',
      logoUrl: null,
      profileName: 'Admin'
    });
  };

  const {
    institutionId,
    institutionName,
    logoUrl
  } = tenantData;

  const handleLogoutClick = () => {
    setShowLogoutConfirm(true);
    setIsMobileMenuOpen(false);
    setIsProfileDropdownOpen(false);
  };

  const handleCancelLogout = () => {
    setShowLogoutConfirm(false);
  };

  const handleConfirmLogout = async () => {
    setIsLoggingOut(true);
    setShowLogoutConfirm(false);
    
    clearTenantData();
    
    const result = await logoutManager.logout({
      redirectTo: '/',
      showToast: true,
      onSuccess: () => {
        console.log('✅ Head of Institution logged out successfully');
      }
    });

    if (!result.success) {
      setTimeout(() => {
        navigate('/');
      }, 500);
    }

    setIsLoggingOut(false);
  };

  const handleNavigate = (path) => {
    navigate(path);
    setIsMobileMenuOpen(false);
    setIsProfileDropdownOpen(false);
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  // Determine display values with proper fallbacks
  const displayName = institutionName && institutionName !== 'Truyon' && institutionName.trim() !== ''
    ? institutionName 
    : 'Institution';

  // Check if logo is valid - using the updated getFullImageUrl logic
  const hasValidLogo = logoUrl && 
                      logoUrl !== 'null' && 
                      logoUrl !== 'undefined' &&
                      logoUrl !== '' &&
                      !logoUrl.includes('null') &&
                      !logoUrl.includes('undefined');

  // Role badge component
  const RoleBadge = () => (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold border ${currentRoleConfig.badgeColor}`}>
      {currentRoleConfig.icon}
      {currentRoleConfig.roleLabel}
    </span>
  );

  // Loading state while fetching data
  if (loading && !tenantData.institutionName) {
    return (
      <div className="min-h-screen flex flex-col bg-slate-50">
        <header className="sticky top-0 z-[100] w-full bg-white border-b border-slate-200/80 shadow-xs flex flex-col">
          <div className="h-0.5 w-full bg-gradient-to-r from-indigo-600 to-blue-600 shrink-0" />
          <div className="w-full px-3 h-12 flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <div className="h-7 w-7 rounded-lg bg-gradient-to-br from-indigo-600 to-blue-600 flex items-center justify-center text-white animate-pulse">
                <School size={14} />
              </div>
              <div className="flex flex-col min-w-0 leading-none">
                <div className="h-3 w-20 bg-gray-200 rounded animate-pulse"></div>
                <div className="h-2 w-16 bg-gray-200 rounded mt-1 animate-pulse"></div>
              </div>
            </div>
            <div className="h-7 w-16 bg-gray-200 rounded-lg animate-pulse"></div>
          </div>
        </header>
        <main className="flex-1 p-3 md:p-6 max-w-[1440px] mx-auto w-full">
          {children}
        </main>
      </div>
    );
  }

  return (
    <>
      <div className="min-h-screen flex flex-col bg-slate-50">
        
        {/* Compact Mobile Header */}
        <header className="sticky top-0 z-[100] w-full bg-white border-b border-slate-200/80 shadow-xs flex flex-col">
          {/* Thin 2px Top Brand Line */}
          <div className="h-0.5 w-full bg-gradient-to-r from-indigo-600 to-blue-600 shrink-0" />

          {/* Core Navigation Bar - Tight h-12 Box */}
          <div className="w-full px-3 h-12 flex items-center justify-between gap-2">
            
            {/* Left Identity Group */}
            <div className="flex items-center gap-2 min-w-0 max-w-[75%]">
              {/* Scaled-down Logo */}
              <div className="shrink-0">
                {hasValidLogo ? (
                  <div className="h-7 w-7 rounded-lg border border-slate-200 p-0.5 bg-white flex items-center justify-center">
                    <img 
                      src={logoUrl} 
                      alt={displayName}
                      className="h-full w-full object-contain rounded-md"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.parentElement.innerHTML = `
                          <div class="h-full w-full rounded-md bg-indigo-600 flex items-center justify-center text-white text-[10px] font-bold">${displayName.charAt(0)}</div>
                        `;
                      }}
                    />
                  </div>
                ) : (
                  <div className="h-7 w-7 rounded-lg bg-gradient-to-br from-indigo-600 to-blue-600 flex items-center justify-center text-white shadow-sm">
                    <School size={14} />
                  </div>
                )}
              </div>

              {/* Identity String Labels */}
              <div className="flex flex-col min-w-0 leading-none">
                <h1 className="text-xs font-bold text-slate-900 truncate tracking-tight">
                  {displayName}
                </h1>
                <div className="flex items-center gap-1.5 mt-0.5">
                  <span className="text-[9px] font-medium text-indigo-600 tracking-wider uppercase">
                    {currentRoleConfig.title}
                  </span>
                  <RoleBadge />
                </div>
              </div>
            </div>

            {/* Right Action Trigger */}
            <div className="shrink-0 flex items-center gap-1.5">
              {/* Logout Button */}
              <button
                onClick={handleLogoutClick}
                disabled={isLoggingOut}
                className="flex items-center justify-center gap-1 h-7 px-2.5 bg-slate-50 hover:bg-red-50 text-slate-700 hover:text-red-600 border border-slate-200 rounded-lg transition-colors disabled:opacity-60 text-[11px] font-semibold"
              >
                {isLoggingOut ? (
                  <svg className="animate-spin h-3 w-3 text-red-600" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" strokeDasharray="32" />
                  </svg>
                ) : (
                  <>
                    <span className="hidden xs:inline">Logout</span>
                    <LogOut size={12} />
                  </>
                )}
              </button>

              {/* Mobile Menu Toggle */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="md:hidden flex items-center justify-center h-7 w-7 rounded-lg hover:bg-slate-100 transition-colors"
              >
                {isMobileMenuOpen ? <X size={16} /> : <Menu size={16} />}
              </button>
            </div>
          </div>

          {/* Micro Metadata Sub-strip */}
          {institutionId && (
            <div className="border-t border-slate-100 bg-slate-50/50 px-3 py-1 flex items-center justify-between gap-4 text-[10px]">
             
              <div className="flex items-center gap-1 text-slate-500 font-medium">
              </div>
            </div>
          )}
        </header>

        {/* Mobile Navigation Drawer */}
        {isMobileMenuOpen && (
          <div 
            className="fixed inset-0 z-50 bg-black/30 backdrop-blur-sm md:hidden"
            onClick={() => setIsMobileMenuOpen(false)}
          >
            <div 
              className="absolute top-14 left-0 right-0 bg-white border-b border-slate-200 shadow-lg rounded-b-2xl mx-2 overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-2 space-y-0.5">
                {navItems.map((item) => (
                  <button
                    key={item.path}
                    onClick={() => handleNavigate(item.path)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
                      isActive(item.path)
                        ? 'bg-blue-600 text-white shadow-md'
                        : 'text-slate-700 hover:bg-slate-100'
                    }`}
                  >
                    <span className="text-base">{item.icon}</span>
                    {item.label}
                    {isActive(item.path) && (
                      <span className="ml-auto text-xs">✓</span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Content Wrapper */}
        <main className="flex-1 p-3 md:p-6 max-w-[1440px] mx-auto w-full">
          {children}
        </main>
      </div>

      {/* Logout Confirmation Modal */}
      {showLogoutConfirm && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-xs flex items-center justify-center z-[1000] p-4">
          <div className="bg-white rounded-xl max-w-xs w-full shadow-lg border border-slate-100 p-4 text-center">
            <div className="w-10 h-10 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-3">
              <LogOut size={18} className="text-red-600" />
            </div>
            <h3 className="text-sm font-bold text-slate-900 mb-1">
              Confirm Logout
            </h3>
            <p className="text-slate-500 text-[11px] mb-4">
              End your {currentRoleConfig.roleLabel} session for <span className="font-semibold text-slate-700">{displayName}</span>?
            </p>
            <div className="flex gap-2">
              <button
                onClick={handleCancelLogout}
                className="flex-1 py-1.5 text-[11px] font-bold bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmLogout}
                disabled={isLoggingOut}
                className="flex-1 py-1.5 text-[11px] font-bold bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:opacity-60"
              >
                {isLoggingOut ? 'Logging out...' : 'Logout'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Layout;